import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'
import { DataSchemaItemFragment } from 'generated/resources/graphql'
import { uniqBy } from 'lodash'

import { observer } from 'mobx-react'

import Selector from 'modules/core/components/Selector'
import BaseTextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { validatePayrollField } from '../lib/validationUtils'

interface Props {
  dataSchemaItem: DataSchemaItemFragment
  field: Field
  onValidationError?: (fieldName: string, error: string | undefined) => void
}

const VALIDATED_FIELDS = [
  'academic_title',
  'place_of_birth',
  'child_tax_allowance',
  'tax_class',
  'factor',
] as const

export const StringField = observer(({ dataSchemaItem, field, onValidationError }: Props) => {
  const value = typeof field.value === 'string' ? field.value : ''
  const [error, setError] = React.useState<string | undefined>(undefined)
  const allowedValues = dataSchemaItem.allowedValues ?? []

  const validateField = React.useCallback(
    (fieldValue: string) => {
      const validationResult = validatePayrollField(dataSchemaItem.fieldName, fieldValue)

      if (!validationResult.isValid) {
        setError(validationResult.error)
        onValidationError?.(dataSchemaItem.fieldName, validationResult.error)
      } else {
        setError(undefined)
        onValidationError?.(dataSchemaItem.fieldName, undefined)
      }
    },
    [dataSchemaItem.fieldName, onValidationError]
  )

  const isValidatedField = (fieldName: string): boolean => {
    return VALIDATED_FIELDS.some((validField) => validField === fieldName)
  }

  const handleChange = React.useCallback(
    (newValue: string) => {
      field.set(String(newValue))

      if (isValidatedField(dataSchemaItem.fieldName)) {
        validateField(newValue)
      }
    },
    [field, dataSchemaItem.fieldName, validateField]
  )

  if (allowedValues.length > 0) {
    const sortedAllowedValues = uniqBy([...allowedValues], 'key').sort((a, b) =>
      a.value.localeCompare(b.value)
    )
    return (
      <Selector.Popover
        blankLabel={i18n.t('employee_form.payroll_data.select_an_option')}
        placeholder={i18n.t('employee_form.payroll_data.select_an_option')}
        searchPlaceholder={dataSchemaItem.label}
        inputStyle="badge"
        label={dataSchemaItem.label}
        onChange={(newValue) => field.set(newValue ? String(newValue) : undefined)}
        value={[field.value]}
        warning=""
        isMultiSelect={false}
      >
        {sortedAllowedValues.map((allowedValue) => (
          <Selector.Option
            key={allowedValue.key}
            value={allowedValue.key}
            label={allowedValue.value}
            searchValue={allowedValue.value}
          >
            {allowedValue.value}
          </Selector.Option>
        ))}
      </Selector.Popover>
    )
  }

  return (
    <Box>
      <BaseTextField
        label={dataSchemaItem.label}
        mandatory={dataSchemaItem.required}
        placeholder={dataSchemaItem.label}
        value={value}
        onChange={handleChange}
        textAlign="left"
        error={error}
      />
    </Box>
  )
})
